/**
 * Parcel/APN validation utilities for fax send operations.
 * 
 * Validates that the order has valid APN data before fax can be sent.
 * Checks multiple canonical sources: parcels[].parcel_apn, parcels[].parcel_id,
 * parcels[].parcel_number, the flat parcel_apns field, tax_certificate parcels,
 * and property.parcel_id.
 */

/**
 * Validate that all parcels on an order have APN values.
 * 
 * @param {Object} orderData - The order object
 * @param {Object} [certificateData] - Optional tax certificate data (loaded separately from order details)
 * @returns {{ valid: boolean, missingParcels: number[], message: string|null }}
 */
export const validateParcelsForFax = (orderData, certificateData = null) => {
  if (!orderData) return { valid: false, missingParcels: [1], message: 'Parcel/APN is required before sending fax request.' };

  const parcels = orderData.parcels || [];
  const missingParcels = [];

  // Check the flat parcel_apns field as a valid APN source (Search Setup field)
  const apnsField = orderData.parcel_apns;
  let hasLegacyApn = false;
  if (Array.isArray(apnsField)) {
    hasLegacyApn = apnsField.some(a => String(a || '').trim() !== '');
  } else if (typeof apnsField === 'string') {
    hasLegacyApn = apnsField.trim() !== '';
  }

  // Check tax certificate sources for APN data
  let hasCertApn = false;
  if (!hasLegacyApn) {
    const tc = orderData.tax_certificate || {};
    // Check tax_certificate.parcel_apns
    const tcApns = tc.parcel_apns;
    if (Array.isArray(tcApns)) {
      hasCertApn = tcApns.some(a => String(a || '').trim() !== '');
    } else if (typeof tcApns === 'string' && tcApns.trim()) {
      hasCertApn = true;
    }
    // Check tax_certificate.parcels[].parcel_apn
    if (!hasCertApn && Array.isArray(tc.parcels)) {
      hasCertApn = tc.parcels.some(p => (p.parcel_apn || p.parcel_id || '').trim() !== '');
    }
    // Check property.parcel_id / property.parcel_apn
    if (!hasCertApn) {
      const prop = orderData.property || {};
      if ((prop.parcel_id || prop.parcel_apn || '').trim()) {
        hasCertApn = true;
      }
    }
    // Check top-level parcel_apn on order
    if (!hasCertApn && (orderData.parcel_apn || '').trim()) {
      hasCertApn = true;
    }
  }

  // Also check separately-loaded certificateData (tax cert API response)
  if (!hasLegacyApn && !hasCertApn && certificateData) {
    // Check certificateData.parcel_apns
    const cdApns = certificateData.parcel_apns;
    if (Array.isArray(cdApns)) {
      hasCertApn = cdApns.some(a => String(a || '').trim() !== '');
    } else if (typeof cdApns === 'string' && cdApns.trim()) {
      hasCertApn = true;
    }
    // Check certificateData.parcels[].parcel_apn
    if (!hasCertApn && Array.isArray(certificateData.parcels)) {
      hasCertApn = certificateData.parcels.some(p => (p.parcel_apn || p.parcel_id || '').trim() !== '');
    }
  }

  const hasAnyApn = hasLegacyApn || hasCertApn;

  if (parcels.length > 0) {
    parcels.forEach((p, i) => {
      if (p.is_active === false) return; // skip inactive parcels
      // Check all possible APN field names
      const apn = (p.parcel_apn || p.parcel_id || p.parcel_number || '').trim();
      // If this parcel has no APN AND no other APN source, it's missing
      if (!apn && !hasAnyApn) missingParcels.push(i + 1); // 1-indexed
    });
  } else if (!hasAnyApn) {
    // No parcels array at all and no APN from any source
    missingParcels.push(1);
  }

  if (missingParcels.length === 0) {
    return { valid: true, missingParcels: [], message: null };
  }

  const message = missingParcels.length === 1 && parcels.length <= 1
    ? 'Parcel/APN is required before proceeding.'
    : `Parcel ${missingParcels.map(n => `#${n}`).join(', ')} missing APN. All parcels must have an APN before proceeding.`;

  return { valid: false, missingParcels, message };
};

// ── TAX-EMAIL-APN-GATE-001 ────────────────────────────────────────────────
// Strict per-parcel check for the Tax Collector *Email* action only.
// Mirrors backend services/parcel_apn_guard.py (messages + precedence).
// `validateParcelsForFax` above is intentionally untouched (any-APN semantics).
export const EMAIL_APN_SINGLE_MSG = 'Parcel/APN is required before sending an email. Please add the Parcel/APN and try again.';
const EMAIL_APN_MULTI_TAIL = 'Every parcel must have a Parcel/APN before sending an email. Please add the Parcel/APN and try again.';

const joinList = (items) => {
  if (items.length <= 1) return items[0] || '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};
const activeParcels = (list) => (Array.isArray(list) ? list : []).filter(p => p && typeof p === 'object' && p.is_active !== false);
const parcelApn = (p) => String(p.parcel_apn || p.parcel_id || p.parcel_number || '').trim();

/**
 * @param {Object} orderData
 * @param {Array|null} liveParcels - the certificate parcels currently being edited (takes precedence)
 * @returns {{ valid: boolean, missingParcels: Array<{index:number,label:string,address:string}>, message: string|null }}
 */
export const validateParcelsForEmail = (orderData, liveParcels = null) => {
  const order = orderData || {};
  let parcels = activeParcels(liveParcels);
  if (!parcels.length) parcels = activeParcels(order.tax_certificate?.parcels);
  if (!parcels.length) parcels = activeParcels(order.parcels);
  if (!parcels.length) {
    const legacy = order.parcel_apns || order.parcel_apn || order.property?.parcel_id || '';
    const single = Array.isArray(legacy) ? (legacy.find(a => String(a || '').trim()) || '') : legacy;
    parcels = [{ parcel_apn: String(single || '').trim() }];
  }
  const orderAddress = String(order.property_address_full || order.property_address || '').trim();
  const missingParcels = [];
  parcels.forEach((p, i) => {
    if (parcelApn(p)) return;
    const address = String(p.property_address || p.address || p.situs_address || '').trim() || orderAddress;
    missingParcels.push({ index: i + 1, label: `Parcel #${i + 1}`, address });
  });
  if (!missingParcels.length) return { valid: true, missingParcels: [], message: null };
  if (parcels.length <= 1) return { valid: false, missingParcels, message: EMAIL_APN_SINGLE_MSG };
  const names = missingParcels.map(m => (m.address ? `${m.label} (${m.address})` : m.label));
  return { valid: false, missingParcels, message: `Parcel/APN is missing for ${joinList(names)}. ${EMAIL_APN_MULTI_TAIL}` };
};

